import React, { useRef, useLayoutEffect } from 'react';
import { gsap, Linear } from 'gsap';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { Columns } from '@components/Columns';
import {
  StyledSection,
  JoyButtonTextPairing,
  StyledBrandImage,
  StyledBrandName,
  BrandsWrapper,
  BrandImageBox,
  BrandImageWrapperBox,
  StyledColumn,
} from './RegistryBrandsSection.styles';
import { registryPageData } from '@data-ts/pages/registry';

const registryBrandsSectionData = registryPageData.registryBrandsSection;

interface RegistryBrandsSectionProps {}

const brandAndLogoList = [
  {
    brandImage: 'registry-v2/registry-brands/image-riedel-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-riedel-v2.svg',
    logoWidth: 40,
    alt: 'Ridel wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-kitchenaid-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-kitchenaid-v2.svg',
    logoWidth: 130,
    alt: 'Kitchen Aid wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-yeti-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-yeti-v2.svg',
    logoWidth: 60,
    logoHeight: 15,
    alt: 'Yeti wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-lecreuset-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-le-creuset-v2.svg',
    logoWidth: 120,
    alt: 'Le Creuset wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-away-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-away-v2.svg',
    logoWidth: 55,
    alt: 'Away wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-airbnb.jpg',
    brandName: 'registry-v2/registry-brands/logo-airbnb.svg',
    logoWidth: 90,
    logoBkgPosition: '12px 0px',
    alt: 'Airbnb wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-calphalon-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-calphalon-v2.svg',
    bkgPosition: '10px 50px',
    logoWidth: 110,
    alt: 'Calphalon wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-cusinart-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-cuisinart-v3.svg',
    logoWidth: 80,
    alt: 'Cuisinart wedding registry',
  },
  {
    brandImage: 'registry-v2/registry-brands/image-dyson-v3.jpg',
    brandName: 'registry-v2/registry-brands/logo-dyson-v3.svg',
    logoWidth: 180,
    bkgPosition: '-43px 55px',
    logoBkgPosition: '23px 0px',
    alt: 'Dyson wedding registry',
  },
];

export const RegistryBrandsSection: React.FC<RegistryBrandsSectionProps> = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController && tagsRef.current) {
          heroTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          heroTimeline.fromTo(
            tagsRef.current,
            {
              transform: 'translateX(-30vw)',
            },
            {
              transform: 'translateX(60vw)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#brands-wrapper',
          triggerHook: 'onEnter',
          duration: '250%',
          offset: 200,
        })
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted]);

  return (
    <StyledSection as="section" ref={sectionRef} id="Registry_Brands" marginTop={[12, 8]} marginBottom={[6, 10]}>
      <StyledColumn verticalAlign="center" centered>
        <Columns.Column width={4}>
          <JoyButtonTextPairing
            alignment="center"
            eyebrow={registryBrandsSectionData.eyebrow}
            eyebrowColor="humana5"
            eyebrowVariant="sectionEyeBrow20"
            eyebrowTagName="h2"
            title={registryBrandsSectionData.title}
            titleVariant="hed40"
            titleColor="humana5"
            titleTagName="h2"
            body={registryBrandsSectionData.body}
            bodyVariant="dek20"
            bodyColor="neutra6"
            bodyTagName="p"
          />
        </Columns.Column>
      </StyledColumn>
      <BrandsWrapper paddingTop={[6, 11]} id="brands-wrapper" ref={tagsRef}>
        <BrandImageWrapperBox>
          {brandAndLogoList.map((item, index) => (
            <BrandImageBox marginX={[6, 6, 6, 6, 11]} key={index} flexDirection="column" alignItems="center">
              <StyledBrandImage alt={item.alt} src={item.brandImage} bkgPosition={item.bkgPosition} />
              <StyledBrandName
                alt=""
                src={item.brandName}
                width={item.logoWidth}
                height={item.logoHeight}
                bkgPosition={item.logoBkgPosition}
              />
            </BrandImageBox>
          ))}
        </BrandImageWrapperBox>
      </BrandsWrapper>
    </StyledSection>
  );
};
